import { useEffect, useRef, useState, type ReactNode } from 'react';
import { BarChart3, Lock, LogOut, Settings, User as UserIcon } from 'lucide-react';
import { Avatar } from '@/components/ui';
import { ThemeSwitcher } from '@/features/theme/components/ThemeSwitcher';
import { useMyCommunities } from '@/features/communities/hooks/useMyCommunities';
import { cn } from '@/lib/utils';
import { useLocker } from '../lockerStore';
import { useAuth } from '@/app/providers';
import { useIncomingFriendRequests } from '../hooks/useSocial';

interface AvatarMenuProps {
  avatarSrc?: string | null;
  displayName?: string | null;
  email?: string | null;
  dropdownClassName?: string;
  onViewProfile: () => void;
  onSettings: () => void;
  onLogout: () => void;
}

/**
 * The TopNav account dropdown (OG `.user-menu-dropdown`, rendered inline in
 * components.js renderTopNav). Avatar trigger → glass popover with identity
 * header, View Profile, Settings, an inline Theme (accent) picker, and a red
 * Log out. Closes on outside-click / Esc. The avatar click opens this menu — it
 * does NOT sign out.
 */
export function AvatarMenu({
  avatarSrc,
  displayName,
  email,
  dropdownClassName,
  onViewProfile,
  onSettings,
  onLogout,
}: AvatarMenuProps) {
  const [open, setOpen] = useState(false);
  const [analyticsTipOpen, setAnalyticsTipOpen] = useState(false);
  const openLocker = useLocker((s) => s.openLocker);
  const { user } = useAuth();
  const { data: incomingRequests = [] } = useIncomingFriendRequests(user?.id);
  const { data: myCommunities } = useMyCommunities();
  const ref = useRef<HTMLDivElement>(null);
  const analyticsUnlocked =
    myCommunities?.some(
      (item) => item.relationship === 'owner' && (item.community.member_seq || 0) >= 50,
    ) ?? false;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const select = (fn: () => void) => () => {
    setOpen(false);
    setAnalyticsTipOpen(false);
    fn();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Account menu"
        aria-haspopup="menu"
        aria-expanded={open}
        className="relative rounded-full transition-transform hover:scale-105"
      >
        <Avatar src={avatarSrc} name={displayName} size={36} />
        {incomingRequests.length > 0 && (
          <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-page bg-red-500 shadow-[0_0_12px_rgba(239,68,68,0.8)]" />
        )}
      </button>

      {open && (
        <div
          className={cn(
            'absolute right-0 z-[200] mt-2.5 min-w-[240px] animate-fade-in overflow-hidden rounded-[14px] border border-white/10 bg-[linear-gradient(165deg,rgba(44,44,58,0.1),rgba(15,15,22,0.08))] p-2 shadow-[0_24px_60px_rgba(0,0,0,0.5)] backdrop-blur-2xl',
            dropdownClassName,
          )}
          role="menu"
        >
          <div className="mb-1.5 flex items-center gap-3 border-b border-edge px-3 pb-3 pt-2.5">
            <Avatar src={avatarSrc} name={displayName} size={38} />
            <div className="min-w-0">
              <div className="truncate text-[13px] font-bold text-hi">
                {displayName ?? 'Your account'}
              </div>
              {email && <div className="truncate text-[11px] text-lo">{email}</div>}
            </div>
          </div>

          <MenuItem
            icon={<UserIcon size={16} strokeWidth={1.8} />}
            label="View Profile"
            trailing={
              incomingRequests.length > 0 ? (
                <span className="h-2.5 w-2.5 rounded-full bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.75)]" />
              ) : null
            }
            onClick={select(onViewProfile)}
          />
          <MenuItem
            icon={<BarChart3 size={16} strokeWidth={1.8} />}
            label="Analytics"
            trailing={
              analyticsUnlocked ? (
                <span className="text-[10px] font-extrabold uppercase tracking-[0.6px] text-accent">
                  Featured
                </span>
              ) : (
                <Lock size={13} strokeWidth={2} className="text-lo" />
              )
            }
            locked={!analyticsUnlocked}
            onClick={() => setAnalyticsTipOpen((v) => !v)}
          />
          <InlineTip open={analyticsTipOpen}>
            {analyticsUnlocked
              ? 'Analytics dashboards are being prepared for featured communities.'
              : 'Must have a community of 50 members to unlock analytics.'}
          </InlineTip>
          <MenuItem
            icon={<Settings size={16} strokeWidth={1.8} />}
            label="Settings"
            onClick={select(onSettings)}
          />

          <div className="mx-2 my-1.5 h-px bg-edge" />
          <ThemeSwitcher
            onOpenLocker={() => {
              setOpen(false);
              openLocker();
            }}
          />
          <div className="mx-2 my-1.5 h-px bg-edge" />

          <MenuItem
            icon={<LogOut size={16} strokeWidth={1.8} />}
            label="Log out"
            danger
            onClick={select(onLogout)}
          />
        </div>
      )}
    </div>
  );
}

function InlineTip({ open, children }: { open: boolean; children: ReactNode }) {
  return (
    <div
      className={cn(
        'grid overflow-hidden transition-[grid-template-rows] duration-300 ease-cinematic',
        open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
      )}
    >
      <div className="min-h-0">
        <div
          className={cn(
            'mx-3 mb-1 px-3 text-[11px] font-semibold leading-[1.45] text-white/40 transition-[opacity,padding] duration-300',
            open ? 'pb-2 pt-0.5 opacity-100' : 'py-0 opacity-0',
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

function MenuItem({
  icon,
  label,
  onClick,
  danger,
  locked,
  trailing,
}: {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  danger?: boolean;
  locked?: boolean;
  trailing?: ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      role="menuitem"
      aria-disabled={locked}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-[13px] font-semibold transition-colors',
        danger ? 'text-[#ff6b6b] hover:bg-[rgba(255,107,107,0.08)]' : 'text-hi hover:bg-white/5',
        locked && 'text-mid',
      )}
    >
      {icon}
      <span className="flex-1">{label}</span>
      {trailing}
    </button>
  );
}
